// EMAIL_BOUNCE_ROUTE — an inbound "delivery failure" notice for mail sent to
// a job's contact on file (classified BOUNCE by utils/proxyEmailClassifier.ts).
// Always surfaces as a Proxy Exception (see isProxyTaskException in
// proxyWorkflowRegistry.ts) — a bounce means the contact on file can't be
// trusted until someone verifies it, the same "needs a closer look" reasoning
// as a missing-solicitor exception, just a different cause.
import type { ProxyJob } from '../mockProxyJobs';
import type { ProxyInboxEmail } from '../mockProxyEmails';
import { classifyEmail } from '../../utils/proxyEmailClassifier';
import type {
  ProxyTask, ProxyWorkflowContext, ProxyWorkflowDefinition, ProxyPanelContext, ProxyReviewPanelSchema,
} from '../proxyWorkflowTypes';

function buildTask(job: ProxyJob, email: ProxyInboxEmail): ProxyTask {
  const cls = classifyEmail(email);
  return {
    id: `bounce-${email.id}`,
    workflowType: 'EMAIL_BOUNCE_ROUTE',
    jobId: job.jobId,
    title: `${job.issuer} - Email bounced`,
    subtitle: `Job ${job.jobId}`,
    reason: cls.why,
    source: 'email',
    sourceEmailId: email.id,
    createdAt: email.date,
    confidence: Math.round(cls.confidence * 100),
  };
}

function buildTasks({ jobs, emails }: ProxyWorkflowContext): ProxyTask[] {
  const tasks: ProxyTask[] = [];
  for (const email of emails) {
    if (!email.relatedJobId) continue;
    if (classifyEmail(email).category !== 'BOUNCE') continue;
    const job = jobs.find(j => j.jobId === email.relatedJobId);
    if (!job || job.followUpHistory.some(r => r.type === 'email_bounce_verify' && r.sourceEmailId === email.id)) continue;
    tasks.push(buildTask(job, email));
  }
  return tasks;
}

function defaultEdits(): Record<string, any> {
  return {}; // nothing here is human-editable
}

function buildPanel({ job, email }: ProxyPanelContext): ProxyReviewPanelSchema {
  const src = email!;
  // The address that actually bounced, as the delivery notice states it.
  const failedAddress = src.body.match(/recipients:\s*(\S+?)\.?\s/)?.[1] ?? job.solicitorEmail ?? 'the address on file';
  const subject = `Contact verification needed - ${job.issuer} (Job ${job.jobId})`;
  const body = `A message to ${job.solicitor ?? 'the contact on file'} (${failedAddress}) bounced back undeliverable. Can you confirm the correct contact so we can resend?`;

  return {
    extractedFields: [
      { key: 'issuer', label: 'Issuer', display: job.issuer },
      { key: 'jobId', label: 'Job #', display: job.jobId },
      { key: 'solicitor', label: 'Solicitor on file', display: job.solicitor ?? 'None' },
      { key: 'failedAddress', label: 'Failed address', display: failedAddress },
    ],
    calculation: {
      title: 'Delivery failure',
      ruleText: src.body,
      rows: [],
      totalRow: { label: 'Status', value: 'Bounced, contact needs verification', tone: 'amber' },
    },
    drafts: [
      { key: 'verify', icon: '📧', label: 'Verify contact → ICT Manager', to: 'ict-manager@betanxt.com', subject, body },
    ],
    approveSummary: [
      'Send the contact-verification request',
      'Log a follow-up on this job',
      'Move this task to Done.',
    ],
    approveConfirmation: `Sent a contact-verification request for <strong>${job.issuer}</strong>. The bounced address needs confirming before Ace resends.`,
    closesTicket: true,
    outboundActions: [
      { key: 'verify', followUpType: 'email_bounce_verify', recipientEmail: 'ict-manager@betanxt.com', subject, bodyPreview: body },
    ],
  };
}

export const EMAIL_BOUNCE_ROUTE_WORKFLOW: ProxyWorkflowDefinition = {
  type: 'EMAIL_BOUNCE_ROUTE',
  label: 'Email Bounced',
  noun: 'a contact-verification request',
  trigger: 'An inbound email classifies as BOUNCE, meaning a delivery-failure notice for mail sent to the job\'s contact on file.',
  source: 'Inbound email (classified by utils/proxyEmailClassifier.ts).',
  steps: ['Source & extraction', 'Delivery failure', 'Verify & approve'],
  buildTasks,
  defaultEdits,
  buildPanel,
};
